import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material';

import {
  CANCEL_ACTION_NAME,
  INTEGRALERT_CONNECT_ACTION_NAME,
  INTEGRALERT_DATA_SOURCE_NAME_LABEL,
  INTEGRALERT_DEFAULT_PAYLOAD_INTERVAL,
  INTEGRALERT_DIALOG_TITLE,
  INTEGRALERT_GATEWAY_KEY_LABEL,
  INTEGRALERT_GATEWAY_KEY_PLACEHOLDER,
  INTEGRALERT_PAYLOAD_INTERVAL_LABEL,
} from '../config/config';

export type ConnectIntegralertValues = {
  sensorGatewayKey: string;
  dataSourceName: string;
  payloadInterval: string;
};

type Props = {
  open: boolean;
  isSaving: boolean;
  onClose: () => void;
  onConfirm: (values: ConnectIntegralertValues) => void;
};

export const IntegralertDialog = ({ open, isSaving, onClose, onConfirm }: Props) => {
  const [sensorGatewayKey, setSensorGatewayKey] = useState('');
  const [dataSourceName, setDataSourceName] = useState('');
  const [payloadInterval, setPayloadInterval] = useState(INTEGRALERT_DEFAULT_PAYLOAD_INTERVAL);

  useEffect(() => {
    if (!open) {
      setSensorGatewayKey('');
      setDataSourceName('');
      setPayloadInterval(INTEGRALERT_DEFAULT_PAYLOAD_INTERVAL);
    }
  }, [open]);

  const isValid =
    sensorGatewayKey.trim().length > 0 &&
    dataSourceName.trim().length > 0 &&
    payloadInterval.trim().length > 0;

  const handleConfirm = () => {
    if (!isValid || isSaving) return;
    onConfirm({
      sensorGatewayKey: sensorGatewayKey.trim(),
      dataSourceName: dataSourceName.trim(),
      payloadInterval: payloadInterval.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: 600, sm: 800 }, maxWidth: 800 } }}
    >
      <DialogTitle>{INTEGRALERT_DIALOG_TITLE}</DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: '2rem' }}>
        <Box
          rowGap={3}
          columnGap={2}
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(3, 1fr)',
          }}
        >
          <TextField
            autoFocus
            fullWidth
            label={INTEGRALERT_GATEWAY_KEY_LABEL}
            placeholder={INTEGRALERT_GATEWAY_KEY_PLACEHOLDER}
            value={sensorGatewayKey}
            onChange={(event) => setSensorGatewayKey(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleConfirm();
            }}
          />
          <TextField
            fullWidth
            label={INTEGRALERT_DATA_SOURCE_NAME_LABEL}
            value={dataSourceName}
            onChange={(event) => setDataSourceName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleConfirm();
            }}
          />
          <TextField
            fullWidth
            label={INTEGRALERT_PAYLOAD_INTERVAL_LABEL}
            value={payloadInterval}
            onChange={(event) => setPayloadInterval(event.target.value.replace(/\D/g, ''))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleConfirm();
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          {CANCEL_ACTION_NAME}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!isValid || isSaving}>
          {INTEGRALERT_CONNECT_ACTION_NAME}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
